import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import {
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Modal,
  IconButton,
  Grid,
  Divider,
  Tooltip,
  Chip,
} from "@mui/material"
import ZoomIn from "@mui/icons-material/ZoomIn"

import MDInput from "components/MDInput"
import MDTypography from "components/MDTypography"
import MDBadge from "components/MDBadge"

import * as actionsFormat from "../../utils/functions/format"
import { Info } from "@mui/icons-material"

const ProductsSearchWithModal = ({
  products,
  onSelectProducts,
  error,
  helperText,
  selectedProductsProp = null,
  disabled = false,
}) => {
  const [selectedProducts, setSelectedProducts] = useState(selectedProductsProp || [])
  const [modalProduct, setModalProduct] = useState(null)
  const [openImageModal, setOpenImageModal] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)

  useEffect(() => {
    setSelectedProducts(selectedProductsProp || [])
  }, [selectedProductsProp])

  const handleProductSelect = (event, newValue) => {
    if (disabled) return
    // console.log("newValue =>", newValue)
    setSelectedProducts(newValue)
    onSelectProducts(newValue)
  }

  const handleCloseModal = () => setModalProduct(null)

  const handleOpenModal = (product) => setModalProduct(product)

  const handleOpenImageModal = (image) => {
    setSelectedImage(image)
    setOpenImageModal(true)
  }

  const handleCloseImageModal = () => {
    setSelectedImage(null)
    setOpenImageModal(false)
  }

  const scrollRef = useRef(null)
  const isDown = useRef(false)
  const startX = useRef(0)
  const scrollLeft = useRef(0)

  return (
    <>
      <Autocomplete
        multiple
        fullWidth
        options={products || []}
        value={selectedProducts}
        onChange={handleProductSelect}
        isOptionEqualToValue={(option, value) => Number(option.id) === Number(value?.id)}
        getOptionLabel={(option) => `${option.name} (${option.reference})`}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              label={`${option.name}`}
              onClick={() => handleOpenModal(option)}
              icon={
                <Tooltip title="Voir les détails">
                  <Info fontSize="small" />
                </Tooltip>
              }
            />
          ))
        }
        renderOption={(props, option, { selected }) => (
          <li
            {...props}
            style={{
              backgroundColor: selected ? "rgba(25, 118, 210, 0.15)" : "inherit",
              borderRadius: 6,
              margin: "2px 0",
              transition: "background-color 0.2s ease",
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Typography variant="body2" fontWeight={selected ? "bold" : 400}>
                {option.name} ({option.reference})
              </Typography>

              <Tooltip title="Voir les détails">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleOpenModal(option)
                  }}
                >
                  <Info fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <MDInput
            {...params}
            label="Rechercher produit..."
            variant="standard"
            margin="normal"
            fullWidth
            error={error}
            helperText={helperText}
            disabled={disabled}
          />
        )}
      />

      <Dialog open={!!modalProduct} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <MDTypography variant="h5" fontWeight="bold">
            Détails du produit
          </MDTypography>

          {modalProduct?.warrantyPeriod && (
            <MDBadge badgeContent={`Garantie: ${modalProduct?.warrantyPeriod} mois`} color="info" variant="gradient" />
          )}
          <MDBadge
            badgeContent={`Promo: ${modalProduct?.isOnPromo ? "Oui" : "Non"}`}
            color={modalProduct?.isOnPromo ? "success" : "error"}
            variant="gradient"
          />
        </DialogTitle>
        <DialogContent dividers>
          {modalProduct && (
            <Box display="flex" flexDirection="column" gap={1}>
              {/* Header */}
              <Box display="flex" flexDirection="row" gap={1} justifyContent="space-between">
                <Box>
                  <MDTypography variant="h5" fontWeight="bold">
                    {modalProduct?.name}
                  </MDTypography>
                  <Typography variant="body2" color="text">
                    Référence : <strong>{modalProduct?.reference}</strong>
                  </Typography>
                  {/*  category and subcategory */}
                  {(modalProduct?.category?.name || modalProduct?.subcategory?.name) && (
                    <>
                      {/* Categories */}
                      <Box>
                        {modalProduct?.category?.name && (
                          <MDTypography variant="body2" color="text">
                            Catégorie : <strong>{modalProduct?.category?.name}</strong>
                          </MDTypography>
                        )}
                        {modalProduct?.subcategory?.name && (
                          <MDTypography variant="body2" color="text">
                            Sous-catégorie : <strong>{modalProduct?.subcategory?.name}</strong>
                          </MDTypography>
                        )}
                      </Box>
                    </>
                  )}
                </Box>
                <Box>
                  {modalProduct?.codeQr && (
                    <>
                      <Box
                        sx={{
                          flex: "0 0 auto",
                          width: 140,
                          height: 140,
                          borderRadius: 2,
                          overflow: "hidden",
                          border: "1px solid #e1e1e1",
                          position: "relative",
                          "&:hover .zoomIcon": { opacity: 1 },
                        }}
                      >
                        <img
                          src={actionsFormat?.uploadPaths?.qrCode(modalProduct.codeQr) ?? "/placeholder.png"}
                          alt={modalProduct.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                    </>
                  )}
                </Box>
              </Box>

              <Divider />

              {/* Price */}
              <Box display="flex" gap={3}>
                <MDTypography variant="body2" color="text">
                  Prix de vente : <strong>{modalProduct?.salePrice} TND</strong>
                </MDTypography>
                {modalProduct?.promoPrice && (
                  <MDTypography variant="body2" color="error">
                    Prix promo : <strong>{modalProduct?.promoPrice} TND</strong>
                  </MDTypography>
                )}
              </Box>

              <Divider />

              {/* Stock */}
              {modalProduct?.stocks?.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                    Stock disponible
                  </Typography>
                  <Grid container spacing={1}>
                    {modalProduct?.stocks.map((stock, i) => (
                      <Grid item xs={12} sm={6} key={i}>
                        <MDTypography variant="body2">
                          Dépôt : <strong>{stock?.depot.name}</strong> — Quantité disponible :{" "}
                          <strong>{stock?.availableQuantity}</strong>
                        </MDTypography>

                        {/* Variantes pour ce dépôt */}
                        {stock?.variants?.length > 0 && (
                          <Box ml={2} mt={1}>
                            {stock.variants.map((variant) => (
                              <Typography key={variant.id} variant="body2" sx={{ display: "block", pl: 1 }}>
                                • {variant.color} | {variant.size} | {variant.gender} —{" "}
                                <strong>{variant.availableQuantity}</strong> dispo
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Description */}
              {modalProduct?.description && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                      Description
                    </Typography>
                    <MDTypography variant="body2" color="text">
                      {modalProduct?.description}
                    </MDTypography>
                  </Box>
                </>
              )}

              {modalProduct?.images?.length > 0 && (
                <>
                  <Divider />
                  {/* Images */}
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 2 }}>
                      Images
                    </Typography>

                    <Box
                      ref={scrollRef}
                      onMouseDown={(e) => {
                        isDown.current = true
                        scrollRef.current.classList.add("active")
                        isDown.current = true
                        startX.current = e.pageX - scrollRef.current.offsetLeft
                        scrollLeft.current = scrollRef.current.scrollLeft
                      }}
                      onMouseLeave={() => {
                        isDown.current = false
                        scrollRef.current.classList.remove("active")
                      }}
                      onMouseUp={() => {
                        isDown.current = false
                        scrollRef.current.classList.remove("active")
                      }}
                      onMouseMove={(e) => {
                        if (!isDown.current) return
                        e.preventDefault()
                        const x = e.pageX - scrollRef.current.offsetLeft
                        const walk = (x - startX.current) * 1.5
                        scrollRef.current.scrollLeft = scrollLeft.current - walk
                      }}
                      sx={{
                        display: "flex",
                        gap: 2,
                        overflowX: "auto",
                        cursor: "grab",
                        pb: 1,
                        "&:active": { cursor: "grabbing" },
                        "&::-webkit-scrollbar": {
                          height: 6,
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#ccc",
                          borderRadius: 3,
                        },
                      }}
                    >
                      {modalProduct.images?.map((img, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            flex: "0 0 auto",
                            width: 140,
                            height: 140,
                            borderRadius: 2,
                            overflow: "hidden",
                            border: "1px solid #e1e1e1",
                            position: "relative",
                            cursor: "pointer",
                            "&:hover .zoomIcon": { opacity: 1 },
                          }}
                          onClick={() => handleOpenImageModal(img)}
                        >
                          <img
                            src={actionsFormat.uploadPaths.productImage(img) ?? "/placeholder.png"}
                            alt={modalProduct.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          <Box
                            className="zoomIcon"
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              color: "#fff",
                              backgroundColor: "rgba(0,0,0,0.5)",
                              borderRadius: "50%",
                              opacity: 0,
                              transition: "opacity 0.3s",
                            }}
                          >
                            <IconButton sx={{ color: "#fff" }}>
                              <ZoomIn fontSize="large" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Zoom Modal */}
      <Modal
        open={openImageModal}
        onClose={handleCloseImageModal}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box sx={{ maxWidth: "100%", maxHeight: "100%" }}>
          <img
            src={selectedImage ? actionsFormat.uploadPaths.productImage(selectedImage) : ""}
            alt="Zoomed"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              borderRadius: 4,
            }}
          />
        </Box>
      </Modal>
    </>
  )
}

ProductsSearchWithModal.propTypes = {
  products: PropTypes.array.isRequired,
}

export default ProductsSearchWithModal
